import { useRef, useState } from 'react';
import type {
  EssActionFailure,
  EssCreateResult,
  UseEssInvitesResult,
} from '../../data/use_ess_invites';

/**
 * Directory-level self-service invite actions: the auto-invite that follows
 * "Add employee", and the per-row Send / Resend / Re-invite button. Both go
 * through `payroll.createEssInvite` (via `useEssInvites().create_invite`),
 * which revokes any earlier pending invite for the employee, so "resend" and
 * "re-invite" are the same server call as a first invite.
 *
 * Outcomes render as ONE dismissible notice above the table (never a raw
 * toast) plus, only when the email could not be sent, the reveal-once link
 * dialog -- the raw link exists nowhere else.
 */
export type DirectoryEssNotice =
  | { kind: 'emailed'; email: string; after_create: boolean }
  /**
   * The invite exists but its email didn't go -- the link dialog carries the
   * link; this notice outlives that dialog so closing it never leaves the
   * employer with no record that the employee wasn't notified.
   */
  | { kind: 'email_failed'; email: string; after_create: boolean }
  | { kind: 'failed'; failure: EssActionFailure; after_create: boolean };

export interface DirectoryEssOutcome {
  notice: DirectoryEssNotice | null;
  /** Set only for `email_sent: false` -- the link the employer must hand over. */
  fallback_link: string | null;
}

/** Pure mapping from a `createEssInvite` result to what the directory shows. */
export function directoryEssOutcome(
  result: EssCreateResult,
  after_create: boolean,
): DirectoryEssOutcome {
  if (result.outcome === 'failure') {
    return {
      notice: { kind: 'failed', failure: result.failure, after_create },
      fallback_link: null,
    };
  }
  if (result.value.email_sent) {
    return {
      notice: {
        kind: 'emailed',
        email: result.value.invited_email,
        after_create,
      },
      fallback_link: null,
    };
  }
  // The email failed; the dialog carries the link, the notice the fact.
  return {
    notice: {
      kind: 'email_failed',
      email: result.value.invited_email,
      after_create,
    },
    fallback_link: result.value.invite_link,
  };
}

export interface DirectoryEssActions {
  /**
   * The employee whose invite is in flight -- that row's button shows a
   * spinner and every other row's is disabled. One mint at a time: a second
   * click on the same row would revoke the first link and send two emails.
   */
  busy_employee_id: string | null;
  notice: DirectoryEssNotice | null;
  dismiss_notice: () => void;
  fallback_link: string | null;
  close_fallback_link: () => void;
  /** `undefined` when the grant can't write -- hide the row buttons. */
  send_invite: ((employee_id: string) => Promise<void>) | undefined;
  /** Follow-up to a successful "Add employee" -- invites or just refreshes. */
  after_employee_created: (
    employee_id: string,
    options: { invite: boolean },
  ) => Promise<void>;
}

export function useDirectoryEssActions(
  ess: UseEssInvitesResult,
): DirectoryEssActions {
  const [busy_employee_id, set_busy_employee_id] = useState<string | null>(
    null,
  );
  const [notice, set_notice] = useState<DirectoryEssNotice | null>(null);
  const [fallback_link, set_fallback_link] = useState<string | null>(null);
  // Synchronous guard alongside `busy_employee_id`: state updates are async,
  // so two clicks inside one frame would both see `null` there.
  const in_flight_ref = useRef(false);

  async function invite(
    employee_id: string,
    after_create: boolean,
  ): Promise<void> {
    const create_invite = ess.create_invite;
    if (in_flight_ref.current) return;
    if (!create_invite) {
      // The invite list hadn't resolved (or the grant is read-only): the
      // employee exists, so refresh and tell the employer to send it by hand.
      ess.retry();
      set_notice({ kind: 'failed', failure: 'error', after_create });
      return;
    }
    set_notice(null);
    in_flight_ref.current = true;
    set_busy_employee_id(employee_id);
    const result = await create_invite(employee_id);
    in_flight_ref.current = false;
    set_busy_employee_id(null);
    // Success already re-fetches inside the hook; a failure after create
    // still needs the new employee's row to appear.
    if (result.outcome === 'failure') ess.retry();
    const outcome = directoryEssOutcome(result, after_create);
    set_notice(outcome.notice);
    set_fallback_link(outcome.fallback_link);
  }

  return {
    busy_employee_id,
    notice,
    dismiss_notice: () => {
      set_notice(null);
    },
    fallback_link,
    close_fallback_link: () => {
      set_fallback_link(null);
    },
    send_invite: ess.create_invite
      ? (employee_id) => invite(employee_id, false)
      : undefined,
    after_employee_created: async (employee_id, options) => {
      if (options.invite) {
        await invite(employee_id, true);
        return;
      }
      ess.retry();
    },
  };
}
